import ProductModel from '../features/product-model';

if (!customElements.get('products-list-component')) {
  customElements.define(
    'products-list-component',
    class ProductsListComponent extends HTMLElement {
      constructor() {
        super();
        this.flktyImages = [];
        this.flktyContent = [];
        this.activeIndex = 0;

        this.resizeEvent = () => {
          this.placeNav();
          this.resizeSlider();
          requestAnimationFrame(() => this.updateLinksFocus());
        };
        this.touchStartEvent = (e) => {
          const {clientX, clientY} = e.touches[0];
          this.touchStart = e.touches.length > 1 || e.target.closest('.flickity-enabled, [data-tabs-nav], [data-model]') ? null : {clientX, clientY};
        };
        this.touchEndEvent = (e) => this.onSwipe(e);
      }

      connectedCallback() {
        this.tabButtons = this.querySelectorAll('[data-tab-button]');
        this.tabDots = this.querySelectorAll('[data-tab-dot]');
        this.tabItems = this.querySelectorAll('[data-tab-item]');
        this.slidersImages = this.querySelectorAll('[data-slider-images]');
        this.slidersContents = this.querySelectorAll('[data-slider-contents]');
        this.navWrapper = this.querySelector('[data-tabs-nav]')?.parentElement;

        this.placeNav();
        this.initButtons();
        this.initSliders();
        this.initShopifyXrLaunch();
        this.listen();
        this.updateLinksFocus();
      }

      listen() {
        document.addEventListener('theme:resize', this.resizeEvent);

        if (this.tabButtons.length > 1) {
          this.addEventListener('touchstart', this.touchStartEvent, {passive: true});
          this.addEventListener('touchend', this.touchEndEvent);
        }
      }

      resizeSlider() {
        if (this.flktyImages.length > 0) {
          requestAnimationFrame(() => {
            this.flktyImages.forEach((flktyImages) => flktyImages.resize());
          });
        }

        if (this.flktyContent.length > 0) {
          requestAnimationFrame(() => {
            this.flktyContent.forEach((flktyContent) => flktyContent.resize());
          });
        }
      }

      placeNav() {
        const target = window.theme.isMobile() ? this.querySelector('.products-list__item--active .products-list__item__contents') : this.tabItems[0];
        if (!target || this.navWrapper.nextElementSibling === target) return;

        const focusedElement = this.navWrapper.contains(document.activeElement) ? document.activeElement : null;
        target.before(this.navWrapper);
        focusedElement?.focus({preventScroll: true});
      }

      updateLinksFocus() {
        this.querySelectorAll('[data-product-content-wrapper] a, .products-list__item__description--truncate a').forEach((link) => {
          const description = link.closest('.products-list__item__description--truncate');
          const isClipped = description && link.getBoundingClientRect().bottom > description.getBoundingClientRect().bottom;
          link.hidden = description && !link.textContent.trim();
          link.tabIndex = isClipped || link.closest('.flickity-enabled [data-product-content-wrapper]:not(.is-selected)') ? -1 : 0;
        });
      }

      onSwipe(e) {
        const {touchStart} = this;
        this.touchStart = null;
        if (!touchStart || e.touches.length > 0 || !window.theme.isMobile()) return;

        const distance = e.changedTouches[0].clientX - touchStart.clientX;
        if (Math.abs(distance) < 50 || Math.abs(distance) < Math.abs(e.changedTouches[0].clientY - touchStart.clientY)) return;

        this.tabButtons[distance < 0 ? this.activeIndex + 1 : this.activeIndex - 1]?.click();
      }

      initButtons() {
        this.tabButtons.forEach((tabButton, index) => {
          this.tabDots[index]?.addEventListener('click', () => tabButton.click());

          tabButton.addEventListener('click', (e) => {
            if (tabButton.classList.contains('products-list__nav__button--active')) {
              return;
            }

            const currentTabAnchor = tabButton.getAttribute('href');
            const currentTab = this.querySelector(currentTabAnchor);
            const currentCircleText = currentTab.querySelector('[data-circle-text]');
            const nav = tabButton.parentElement;
            const {scrollLeft} = nav;

            this.tabButtons.forEach((button) => {
              button.classList.remove('products-list__nav__button--active');
            });
            this.tabItems.forEach((item) => {
              const circleText = item.querySelector('[data-circle-text]');
              item.classList.remove('products-list__item--active');
              circleText?.classList.add('is-disabled');

              if (theme.settings.animationsEnabled) {
                item.querySelectorAll('.aos-animate:not([data-tab-button])').forEach((element) => {
                  element.classList.remove('aos-animate');
                  setTimeout(() => {
                    element.classList.add('aos-animate');
                  });
                });
              }
            });

            tabButton.classList.add('products-list__nav__button--active');
            currentTab.classList.add('products-list__item--active');
            this.placeNav();
            this.tabDots.forEach((dot, idx) => dot.classList.toggle('is-selected', idx === index));
            nav.scrollLeft = scrollLeft;
            const left = tabButton.offsetLeft - nav.firstElementChild.offsetLeft;
            const padding = parseFloat(getComputedStyle(nav).paddingLeft);
            nav.scrollTo({left: index < this.activeIndex ? left + tabButton.offsetWidth + padding * 2 - nav.clientWidth : left, behavior: 'smooth'});
            this.activeIndex = index;

            document.dispatchEvent(new CustomEvent('theme:resize')); // Trigger theme:resize event to refresh the slider height

            if (currentCircleText) {
              currentCircleText.classList.remove('is-disabled');
              document.dispatchEvent(new CustomEvent('theme:scroll')); // Trigger theme:scroll event to refresh the circle-text values
            }
            e.preventDefault();
          });
        });
      }

      initSliders() {
        this.slidersImages.forEach((sliderImages, idx) => {
          const contentsElement = sliderImages.closest('[data-tab-item]').querySelector('[data-slider-contents]');

          const flktyImages = new window.theme.Flickity(sliderImages, {
            fade: true,
            pageDots: false,
            prevNextButtons: true,
            wrapAround: true,
            adaptiveHeight: true,
            asNavFor: contentsElement,
            on: {
              ready: () => {
                requestAnimationFrame(() => {
                  document.dispatchEvent(new CustomEvent('theme:resize'));
                });
              },
              change: (index) => {
                if (this.flktyContent.length > 0) {
                  this.flktyContent[idx].select(index);
                }
              },
            },
          });

          flktyImages.on('settle', (index) => {
            const elements = sliderImages.querySelectorAll('[data-product-single-media-wrapper]');

            for (let i = 0; i < elements.length; i++) {
              if (i === index) {
                elements[i].querySelector('[data-product-single-media-image]').removeAttribute('tabindex');
              } else {
                elements[i].querySelector('[data-product-single-media-image]').setAttribute('tabindex', '-1');
              }
            }
          });

          this.flktyImages.push(flktyImages);
        });

        this.slidersContents.forEach((sliderContent) => {
          const flktyContent = new window.theme.Flickity(sliderContent, {
            fade: true,
            pageDots: false,
            prevNextButtons: false,
            wrapAround: true,
            adaptiveHeight: true,
            on: {
              ready: () => {
                requestAnimationFrame(() => {
                  document.dispatchEvent(new CustomEvent('theme:resize'));
                });
              },
            },
          });

          flktyContent.on('settle', () => this.updateLinksFocus());

          this.flktyContent.push(flktyContent);
        });
      }

      initShopifyXrLaunch() {
        document.addEventListener('shopify_xr_launch', () => {
          const currentMedia = this.querySelector('[data-model]:not(.media--hidden)');
          currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
        });
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.resizeEvent);
      }
    }
  );
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
